/**
 * Consent-gated session replay.
 *
 * PostHog is initialised in public/index.html with `disable_session_recording:
 * true`, so nothing is recorded until this module explicitly starts it. That is
 * deliberate: replay on an invitation page captures other people's personal data
 * (guest names, phone numbers, dietary needs, private notes to the hosts, the
 * venue address), and previously it began the moment the page loaded, including
 * inside the mobile app's cross-origin iframe, with no way to decline.
 *
 * Product analytics events (page views, feature usage) still flow, because those
 * do not capture the contents of the page. Only replay is gated.
 */

const CONSENT_KEY = "wg_replay_consent_v1";

export const CONSENT_GRANTED = "granted";
export const CONSENT_DENIED = "denied";

function ph() {
  return typeof window !== "undefined" ? window.posthog : undefined;
}

export function readConsent() {
  try {
    const value = localStorage.getItem(CONSENT_KEY);
    return value === CONSENT_GRANTED || value === CONSENT_DENIED ? value : null;
  } catch {
    // Private browsing with storage blocked: treat as undecided but never record.
    return null;
  }
}

/**
 * Guest invitation routes are excluded outright.
 *
 * A guest is not our user, did not sign up, and is looking at someone else's
 * personal data. Asking them for replay consent to view an invitation is not a
 * reasonable trade, so replay is simply never offered or enabled there.
 */
export function isSensitiveRoute(pathname = typeof window !== "undefined" ? window.location.pathname : "") {
  return /^\/i\//.test(pathname || "");
}

export function shouldAskForConsent(pathname) {
  if (isSensitiveRoute(pathname)) return false;
  if (!ph()) return false;
  return readConsent() === null;
}

export function startReplay() {
  const posthog = ph();
  if (!posthog || typeof posthog.startSessionRecording !== "function") return;
  try {
    posthog.startSessionRecording();
  } catch {
    /* replay is optional; never break the app for it */
  }
}

export function stopReplay() {
  const posthog = ph();
  if (!posthog || typeof posthog.stopSessionRecording !== "function") return;
  try {
    posthog.stopSessionRecording();
  } catch {
    /* no-op */
  }
}

export function setConsent(value) {
  const normalized = value === CONSENT_GRANTED ? CONSENT_GRANTED : CONSENT_DENIED;
  try {
    localStorage.setItem(CONSENT_KEY, normalized);
  } catch {
    /* proceed for this page load only */
  }
  if (normalized === CONSENT_GRANTED) startReplay();
  else stopReplay();
  return normalized;
}

/**
 * Apply the stored decision on load. Call once, high in the tree.
 * Returns the decision that was applied, or null when undecided.
 */
export function applyStoredConsent(pathname) {
  if (isSensitiveRoute(pathname)) {
    stopReplay();
    return CONSENT_DENIED;
  }
  const consent = readConsent();
  if (consent === CONSENT_GRANTED) startReplay();
  return consent;
}
